if (!customElements.get('vaillant-mobile-purchase-bar')) {
  customElements.define(
    'vaillant-mobile-purchase-bar',
    class VaillantMobilePurchaseBar extends HTMLElement {
      constructor() {
        super();
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.frameRequested = false;
        this.handleViewportChange = this.requestVisibilityUpdate.bind(this);
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.infoColumn = this.productInfo?.querySelector('.vaillant-product__info-column');
        this.sizeButton = this.querySelector('[data-mobile-product-size]');
        this.submitButton = this.querySelector('[data-mobile-product-submit]');
        this.submitLabel = this.querySelector('[data-mobile-product-submit-label]');

        if (!this.productInfo || !this.infoColumn || !this.sizeButton || !this.submitButton) return;

        this.sizeButton.addEventListener('click', () => this.showSizePicker());
        this.submitButton.addEventListener('click', () => this.submitProduct());
        window.addEventListener('scroll', this.handleViewportChange, { passive: true });
        window.addEventListener('resize', this.handleViewportChange, { passive: true });
        this.mobileQuery.addEventListener?.('change', this.handleViewportChange);

        this.sourceObserver = new MutationObserver(() => this.syncSubmitButton());
        this.soldOutObserver = new MutationObserver(() => this.syncSubmitButton());
        this.soldOutObserver.observe(this.productInfo, {
          attributes: true,
          attributeFilter: ['class'],
        });
        this.observeSourceSubmit();
        this.syncSubmitButton();
        this.updateVisibility();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.handleViewportChange);
        window.removeEventListener('resize', this.handleViewportChange);
        this.mobileQuery.removeEventListener?.('change', this.handleViewportChange);
        this.sourceObserver?.disconnect();
        this.soldOutObserver?.disconnect();
      }

      isSoldOut() {
        return this.productInfo?.classList.contains('vaillant-product-section--sold-out');
      }

      getSourceSubmit() {
        if (this.isSoldOut()) {
          return this.productInfo?.querySelector('.vaillant-product__waitlist-button');
        }

        return this.productInfo?.querySelector('.vaillant-product__add-to-bag');
      }

      getWaitlistForm() {
        return this.productInfo?.querySelector('vaillant-product-waitlist');
      }

      observeSourceSubmit() {
        const sourceSubmit = this.getSourceSubmit();
        if (sourceSubmit === this.sourceSubmit) return;

        this.sourceObserver.disconnect();
        this.sourceSubmit = sourceSubmit;

        if (sourceSubmit) {
          this.sourceObserver.observe(sourceSubmit, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled', 'class'],
            childList: true,
            subtree: true,
          });
        }
      }

      syncSubmitButton() {
        this.observeSourceSubmit();

        if (this.isSoldOut()) {
          const waitlistButton = this.productInfo?.querySelector('.vaillant-product__waitlist-button');
          this.submitButton.disabled = !waitlistButton;
          this.submitButton.classList.remove('is-loading');
          this.submitLabel.textContent =
            waitlistButton?.querySelector('.vaillant-product__waitlist-button-label')?.textContent?.trim() ||
            waitlistButton?.textContent?.trim() ||
            'JOIN THE WAITING LIST';
          return;
        }

        if (!this.sourceSubmit) {
          this.submitButton.disabled = true;
          return;
        }

        const isDisabled =
          this.sourceSubmit.disabled ||
          this.sourceSubmit.getAttribute('aria-disabled') === 'true' ||
          this.sourceSubmit.classList.contains('loading');
        const sourceLabel = this.sourceSubmit.querySelector('span')?.textContent?.trim();

        this.submitButton.disabled = isDisabled;
        this.submitButton.classList.toggle('is-loading', this.sourceSubmit.classList.contains('loading'));
        if (sourceLabel) this.submitLabel.textContent = sourceLabel;
      }

      showSizePicker() {
        const mirrorButton = document.getElementById('mirror-open-button');
        if (mirrorButton) {
          mirrorButton.click();
          return;
        }

        const sizePicker =
          this.productInfo.querySelector('.vaillant-product__option--size') ||
          this.productInfo.querySelector('.vaillant-product__info-column');

        sizePicker?.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start',
        });

        window.setTimeout(() => {
          sizePicker?.querySelector('input:not(:disabled)')?.focus({ preventScroll: true });
        }, 450);
      }

      submitProduct() {
        this.observeSourceSubmit();
        const sourceSubmit = this.getSourceSubmit();
        if (!sourceSubmit) return;

        if (this.isSoldOut()) {
          const waitlistForm = this.getWaitlistForm();
          const emailInput = waitlistForm?.querySelector('input[type="email"]');

          waitlistForm?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'center',
          });

          if (emailInput?.value.trim()) {
            waitlistForm?.querySelector('form')?.requestSubmit();
            return;
          }

          emailInput?.focus({ preventScroll: true });
          return;
        }

        if (sourceSubmit.disabled) return;
        sourceSubmit.click();
      }

      requestVisibilityUpdate() {
        if (this.frameRequested) return;
        this.frameRequested = true;

        window.requestAnimationFrame(() => {
          this.frameRequested = false;
          this.updateVisibility();
          this.syncSubmitButton();
        });
      }

      updateVisibility() {
        const isMobile = this.mobileQuery.matches;
        const hasReachedInfo = this.infoColumn.getBoundingClientRect().top <= window.innerHeight;
        const shouldHide = !isMobile || hasReachedInfo;

        this.classList.toggle('is-hidden', shouldHide);
        this.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
        this.inert = shouldHide;
      }
    }
  );
}
